/**
 * Where dice and generators get their numbers (docs/PARITY-STEP-2-PLAN.md decision 4).
 * `JavaRandom` is Java's `java.util.Random`, bit for bit, so a seeded roll, table pick or
 * generated name comes out the same as on the desktop; `secureRandom` is for live rolls,
 * as the desktop's Dice Roller uses `SecureRandom`.
 */
export interface Random {
  /** A whole number from 0 to `bound - 1`, as `java.util.Random.nextInt(int bound)`. */
  nextInt(bound: number): number;
  /** A number from 0 (included) to 1 (not), as `java.util.Random.nextDouble()`. */
  nextDouble(): number;
}

const MULTIPLIER = 0x5deece66dn;
const ADDEND = 0xbn;
const MASK = (1n << 48n) - 1n;

export class JavaRandom implements Random {
  private seed: bigint;

  constructor(seed: number | bigint) {
    this.seed = (BigInt.asIntN(64, BigInt(seed)) ^ MULTIPLIER) & MASK;
  }

  /** `Random.next(int bits)`: the next `bits` bits, as a signed 32-bit int. */
  next(bits: number): number {
    this.seed = (this.seed * MULTIPLIER + ADDEND) & MASK;
    return Number(BigInt.asIntN(32, this.seed >> BigInt(48 - bits)));
  }

  nextInt(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0 || bound > 0x7fffffff) throw new RangeError('bound must be positive');
    let r = this.next(31);
    const m = bound - 1;
    if ((bound & m) === 0) {
      // A power of two: the high bits, as (int) ((bound * (long) r) >> 31).
      return Number((BigInt(bound) * BigInt(r)) >> 31n);
    }
    // Java's `for (int u = r; u - (r = u % bound) + m < 0; u = next(31));`, the sum wrapping as an int.
    let u = r;
    for (;;) {
      r = u % bound;
      if (((u - r + m) | 0) >= 0) return r;
      u = this.next(31);
    }
  }

  nextDouble(): number {
    return (this.next(26) * 2 ** 27 + this.next(27)) * 2 ** -53;
  }
}

/** The browser's secure random numbers, unbiased. */
export const secureRandom: Random = {
  nextInt(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0 || bound > 0x7fffffff) throw new RangeError('bound must be positive');
    const limit = Math.floor(0x100000000 / bound) * bound;
    const buffer = new Uint32Array(1);
    for (;;) {
      crypto.getRandomValues(buffer);
      if (buffer[0]! < limit) return buffer[0]! % bound;
    }
  },
  nextDouble(): number {
    const buffer = new Uint32Array(2);
    crypto.getRandomValues(buffer);
    return ((buffer[0]! >>> 6) * 2 ** 27 + (buffer[1]! >>> 5)) * 2 ** -53;
  },
};
